import React, { useCallback, useMemo, useRef, useState } from "react";
import MessageCenterConversationList from "./messages-navigator-conversation-list";
import MessageCenterReader from "./messages-reader";
import MessageCenterNavigatorHeader from "./messages-navigator-header";
import MessageCenterContactList from "./messages-navigator-contact-list";
import SeatchResult from "./search-result";
import { inject, observer } from "mobx-react";
import MessageStore from "./stores/messages/MessageStore";
import LayoutStore from "library/core/stores/layout/LayoutStore";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindFab from "library/components/_tailwind/fab";
import TailwindIcon from "library/components/_tailwind/icon";
import TailwindText from "library/components/_tailwind/text";
import {
  ConversationListFilter,
  MessageTabEnum,
} from "./stores/messages/consts";
import { MessageCenterConversationDetails } from "./stores/messages/types";
import { useHistory } from "react-router-dom";
import { AppCommonRouteKey } from "core/stores/route/enums";
import RouteStore from "core/stores/route/RouteStore";
import { WrappedComponentProps, injectIntl } from "react-intl";

type Props = {
  goBack: () => void;
  messageType: MessageTabEnum.POLICY | MessageTabEnum.MESSAGE;
  messageStore?: MessageStore;
  layoutStore?: LayoutStore;
  routeStore?: RouteStore;
};

const MessageCenterNavigator: React.ComponentType<
  Props & WrappedComponentProps
> = ({ messageStore, layoutStore, routeStore, goBack, messageType, intl }) => {
  const history = useHistory();
  const { getSiteRouteURL } = routeStore!;
  const { isMobileScreenSize, isMobileDevice } = layoutStore!;
  const {
    conversations,
    filteredConversations,
    newsAndPolicies,
    activeConversation,
    selectedConversations,
    setSelectedConversations,
    isComposing,
    setIsComposing,
    setIsBulkMessageView,
    isSearchingView,
    isConversationsLoading,
    isSearchingConversations,
    contacts,
    isUnreadFilterActive,
    setConversationListFilter,
  } = messageStore!;
  // Toggle FAB via ref, not state, to avoid list re-renders on scroll.
  const composingFabRef = useRef<HTMLDivElement | null>(null);
  const isComposingFabHiddenRef = useRef<boolean>(false);
  const setComposingFabNode = useCallback((node: HTMLDivElement | null) => {
    composingFabRef.current = node;
    isComposingFabHiddenRef.current = false;
    if (node) {
      node.style.opacity = "1";
      node.style.pointerEvents = "auto";
    }
  }, []);
  // const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [searchResultMatchesUsername, setSearchResultMatchesUsername] =
    useState<string>("");
  const [searchResultMatchesCount, setSearchResultMatchesCount] =
    useState<number>(0);
  const [searchedMessagesView, setSearchedMessagesView] =
    useState<boolean>(false);

  const hasSelectedConversation = useMemo(
    () => !!activeConversation,
    [activeConversation, selectedConversations]
  );

  const showBackButton = useMemo(() => {
    return (
      (isMobileScreenSize && hasSelectedConversation) ||
      isComposing ||
      searchResultMatchesCount > 0
    );
  }, [
    isMobileScreenSize,
    hasSelectedConversation,
    isComposing,
    isSearchingView,
    searchResultMatchesCount,
  ]);

  const toggleIsComposing = useCallback(() => {
    if (isMobileDevice) {
      history.push(
        `${getSiteRouteURL(AppCommonRouteKey.messages)}${getSiteRouteURL(
          AppCommonRouteKey.contacts
        )}`
      );
    } else {
      setIsComposing(!isComposing);
      setIsBulkMessageView(false);
    }
  }, [isComposing]);

  const hasSelectedAllConversations = useMemo(() => {
    return (
      selectedConversations?.length > 0 &&
      filteredConversations?.length === selectedConversations?.length
    );
  }, [selectedConversations, filteredConversations, newsAndPolicies]);

  const selectAllOrNoneConversations = useCallback(() => {
    if (hasSelectedAllConversations) {
      setSelectedConversations([]);
    } else {
      const allConversationIds = filteredConversations.map(
        conversation => conversation.id
      );
      setSelectedConversations(allConversationIds);
    }
  }, [hasSelectedAllConversations, filteredConversations, newsAndPolicies]);

  const setComposingFabHidden = useCallback((hidden: boolean) => {
    if (isComposingFabHiddenRef.current === hidden) {
      return;
    }
    isComposingFabHiddenRef.current = hidden;
    const el = composingFabRef.current;
    if (!el) {
      return;
    }
    // No visibility toggle: it kills iOS momentum-scroll mid-flick.
    el.style.opacity = hidden ? "0" : "1";
    el.style.pointerEvents = hidden ? "none" : "auto";
  }, []);

  // Stable identities so the observer list's memo bail-out fires during scroll.
  const onConversationListScrollUp = useCallback(() => {
    setComposingFabHidden(false);
  }, [setComposingFabHidden]);

  const onConversationListScrollDown = useCallback(() => {
    setComposingFabHidden(true);
  }, [setComposingFabHidden]);

  const onConversationListScrollEnd = useCallback(() => {
    setComposingFabHidden(false);
  }, [setComposingFabHidden]);

  const onMultipleMatchesViewShown = useCallback(
    (messages: MessageCenterConversationDetails[]) => {
      setSearchResultMatchesCount(messages?.length);
      setSearchResultMatchesUsername(
        !messages?.length ? "" : messages?.[0].participants?.[0].username
      );
    },
    []
  );

  return (
    <TailwindFlex
      flexDirection={"flex-col"}
      flexGrow={"flex-grow"}
      textColor='text-main-color'
      margin={isMobileDevice ? ["m-0"] : []}
      padding={isMobileDevice ? ["pr-0"] : ["pr-4"]}
      overflow={["overflow-hidden"]}>
      {messageType === MessageTabEnum.MESSAGE && (
        <>
          <TailwindFlex
            borderWidth={
              !isMobileDevice &&
              ((isComposing && !contacts?.length) ||
                isSearchingConversations ||
                (!isComposing && isConversationsLoading) ||
                (!isComposing && !conversations?.length))
                ? ["border-b-2"]
                : []
            }
            borderColor={"border-primary-bg-color"}
            padding={["p-0"]}
            margin={["m-0"]}>
            <MessageCenterNavigatorHeader
              conversation={activeConversation}
              goBack={() => {
                if (searchResultMatchesCount) {
                  setSearchedMessagesView(false);
                } else {
                  goBack();
                }
              }}
              showBackButton={showBackButton}
              selectedConversations={selectedConversations}
              toggleIsComposing={toggleIsComposing}
              selectAllOrNoneConversations={selectAllOrNoneConversations}
              hasSelectedAllConversations={hasSelectedAllConversations}
              showSearchResultHeader={searchResultMatchesCount > 0}
              searchResultMatchesUsername={searchResultMatchesUsername}
            />
          </TailwindFlex>
          {searchResultMatchesCount ? (
            <TailwindFlex
              backgroundColor={"bg-lightBlue"}
              padding={["px-4", "pt-3", "pb-2"]}
              borderWidth={["border-t-2"]}
              borderColor={"border-primary-bg-color"}
              flexDirection={"flex-row"}>
              {intl.formatMessage(
                {
                  id: "message-center.number-of-matched-messages",
                  defaultMessage: "{count} matched messages",
                },
                {
                  count: searchResultMatchesCount,
                }
              )}
            </TailwindFlex>
          ) : null}
          {isUnreadFilterActive &&
            !isComposing &&
            !isSearchingView &&
            !searchResultMatchesCount && (
              <TailwindFlex
                textColor='text-white'
                padding={["px-4", "py-2"]}
                borderWidth={["border-t-2"]}
                borderColor={"border-primary-bg-color"}
                flexDirection={"flex-row"}
                justifyContent={"justify-between"}
                alignItems={"items-center"}
                style={{ backgroundColor: "#F2A03A" }}>
                <TailwindText fontWeight={"font-bold"}>
                  {intl.formatMessage({
                    id: "message-center.filter-by-unread",
                    defaultMessage: "Filter by: Unread",
                  })}
                </TailwindText>
                <TailwindIcon
                  name={"close"}
                  fontSize={"text-xl"}
                  cursor={"cursor-pointer"}
                  onClick={() =>
                    setConversationListFilter(ConversationListFilter.ALL)
                  }
                />
              </TailwindFlex>
            )}
        </>
      )}

      <TailwindFlex
        borderRadius={isMobileDevice ? "rounded-none" : "rounded-b-lg"}
        flexGrow={"flex-grow"}
        overflow={"overflow-hidden"}
        style={{ height: "calc(100% - 3.5rem)" }}>
        {isComposing && <MessageCenterContactList isMobile={isMobileDevice} />}
        {isSearchingView && <SeatchResult goBack={goBack} />}
        {!isComposing && !isSearchingView && (
          <React.Fragment>
            {(!isMobileDevice || !activeConversation) && (
              <MessageCenterConversationList
                isMobile={isMobileScreenSize}
                onScrollUp={onConversationListScrollUp}
                onScrollDown={onConversationListScrollDown}
                onScrollReset={onConversationListScrollUp}
                onScrollEnd={onConversationListScrollEnd}
                type={messageType}
                onMultipleMatchesViewShown={onMultipleMatchesViewShown}
                searchedMessagesView={searchedMessagesView}
                setSearchedMessagesView={setSearchedMessagesView}
              />
            )}
            {isMobileDevice && activeConversation && (
              <MessageCenterReader goBack={goBack} />
            )}
          </React.Fragment>
        )}
      </TailwindFlex>
      {!isComposing &&
        !isSearchingView &&
        isMobileDevice &&
        !activeConversation &&
        messageType === MessageTabEnum.MESSAGE && (
          <div
            ref={setComposingFabNode}
            style={{ transition: "opacity 0.2s ease" }}>
            <TailwindFab
              backgroundColor={"bg-modal-button-color"}
              size={"lg"}
              zIndex={"z-100"}
              onClick={toggleIsComposing}
              style={{
                right: "15px",
                bottom: "15px",
              }}
            />
          </div>
        )}
    </TailwindFlex>
  );
};
export default injectIntl(
  inject(
    "layoutStore",
    "messageStore",
    "routeStore"
  )(observer(MessageCenterNavigator))
);
